import { z } from 'zod';
import { eventPayloadSchema, eventSchema } from './events';
import { type Scenario, scenarioSchema } from './scenario';

export const FILE_VERSION = 1;

export const scenarioFileSchema = z.object({
  version: z.literal(FILE_VERSION),
  scenario: scenarioSchema,
});

export const runRecordSchema = z.object({
  id: z.string().min(1),
  scenarioId: z.string().min(1),
  scenario: scenarioSchema,
  startedAt: z.number(),
  events: z.array(eventSchema),
  finishedAt: z.number().optional(),
});
export type RunRecord = z.infer<typeof runRecordSchema>;

export const runFileSchema = z.object({
  version: z.literal(FILE_VERSION),
  run: runRecordSchema,
});

function parseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    throw new Error('Datei ist kein gültiges JSON.');
  }
}

function describeIssue(issue: z.ZodIssue): string {
  const path = issue.path.join('.');
  if (issue.code === 'invalid_literal' && path === 'version') {
    return `Version wird nicht unterstützt (erwartet ${FILE_VERSION}).`;
  }
  return `${path || 'Datei'}: ${issue.message}`;
}

// Input pinned to unknown so T is inferred from Output alone: a schema with a
// preprocess step (like triageCategorySchema) has an Input narrower than its Output,
// which would otherwise pull T towards the Input side.
function parseWith<T>(schema: z.ZodType<T, z.ZodTypeDef, unknown>, text: string): T {
  const result = schema.safeParse(parseJson(text));
  if (!result.success) {
    throw new Error(result.error.issues.map(describeIssue).join('\n'));
  }
  return result.data;
}

// Shared with the editor (save and export both call this before writing), so a structurally
// broken scenario (empty name, non-positive patient number, ...) reads the same way whether it
// is caught on import or before it is ever saved.
export function describeScenarioProblems(scenario: Scenario): string[] {
  const result = scenarioSchema.safeParse(scenario);
  if (result.success) return [];
  return result.error.issues.map(describeIssue);
}

export function parseScenarioFile(text: string): Scenario {
  const scenario = parseWith(scenarioFileSchema, text).scenario;
  const problems = validateInjectEffects(scenario);
  if (problems.length) throw new Error(problems.join('\n'));
  return scenario;
}

export function parseRunFile(text: string): RunRecord {
  return parseWith(runFileSchema, text).run;
}

export function serializeScenario(scenario: Scenario): string {
  return JSON.stringify({ version: FILE_VERSION, scenario }, null, 2);
}

export function serializeRun(run: RunRecord): string {
  return JSON.stringify({ version: FILE_VERSION, run }, null, 2);
}

// Inject effects are typed as unknown in scenario.ts to avoid a circular schema import.
export function validateInjectEffects(scenario: Scenario): string[] {
  const problems: string[] = [];
  for (const inject of scenario.injects) {
    for (const [i, effect] of (inject.effects ?? []).entries()) {
      const result = eventPayloadSchema.safeParse(effect);
      if (!result.success) {
        problems.push(
          `Einspieler "${inject.title}", Wirkung ${i + 1}: ${result.error.issues[0]?.message}`,
        );
      }
    }
  }
  return problems;
}
